import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { StockLevelControllerService } from '../api/services/StockLevelControllerService';
import type { StockLevelDto } from '../api/models/StockLevelDto';
import { useToast } from '../hooks/useToast';
import { unitLabel } from '../utils/unitLabel';

interface Props {
  isOpen: boolean;
  materialUuid: string;
  materialName: string;
  baseUnit?: string | null;
  currentStock?: number | null;
  onClose: () => void;
  onSaved: (newLevel: StockLevelDto) => void;
}

export default function IntermediateStockInventoryDialog({
  isOpen,
  materialUuid,
  materialName,
  baseUnit,
  currentStock,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation(['common']);
  const { showToast } = useToast();
  const [value, setValue] = useState<number | null>(currentStock ?? 0);
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (value === null || value < 0) {
      showToast('warning', t('inventory.title', 'Inventur'), t('inventory.invalidValue', 'Bitte einen gültigen Bestandswert eingeben.'));
      return;
    }
    setSaving(true);
    try {
      // The backend accepts sourceType + note; the generated StockLevelDto has not been regenerated yet.
      const payload: StockLevelDto & { sourceType?: string; note?: string } = {
        materialId: materialUuid,
        currentStock: value,
        sourceType: 'MANUAL',
        note: note.trim() || undefined,
      };
      const created = await StockLevelControllerService.create(payload);
      showToast('success', t('inventory.title', 'Inventur'), t('inventory.saved', 'Bestand gespeichert.'));
      onSaved(created);
      onClose();
    } catch {
      showToast('error', t('inventory.title', 'Inventur'), t('inventory.error', 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogComponent
      width="480px"
      isModal={true}
      visible={isOpen}
      header={`${t('inventory.title', 'Inventur')} – ${materialName}`}
      showCloseIcon={true}
      closeOnEscape={true}
      allowDragging={true}
      target="#root"
      animationSettings={{ effect: 'None' }}
      beforeClose={(e) => {
        if (saving) {
          e.cancel = true;
          return;
        }
        e.cancel = true;
        onClose();
      }}
    >
      <div className="p-4 flex flex-col gap-4">
        {currentStock != null && (
          <div className="text-sm text-gray-600">
            {t('inventory.currentValue', 'Aktueller Bestand:')}{' '}
            <span className="font-medium text-gray-900">
              {currentStock} {unitLabel(baseUnit)}
            </span>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('inventory.newValue', 'Neuer Bestand')} {baseUnit ? `(${unitLabel(baseUnit)})` : ''}
          </label>
          <NumericTextBoxComponent
            value={value ?? 0}
            min={0}
            decimals={3}
            format="n3"
            change={(args: any) => setValue(args?.value ?? null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('inventory.note', 'Notiz')} ({t('common.optional', 'optional')})
          </label>
          <TextBoxComponent
            value={note}
            multiline={true}
            placeholder={t('inventory.notePlaceholder', 'z. B. Inventur 26.04., Schwund, Ausschuss …')}
            input={(args: any) => setNote(args?.value ?? '')}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <ButtonComponent onClick={onClose} disabled={saving}>
            {t('common.cancel', 'Abbrechen')}
          </ButtonComponent>
          <ButtonComponent
            isPrimary={true}
            onClick={handleSave}
            disabled={saving || value === null || value < 0}
          >
            {saving ? t('common.saving', 'Speichere …') : t('common.save', 'Speichern')}
          </ButtonComponent>
        </div>
      </div>
    </DialogComponent>
  );
}
